import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/repository/axios';
import { useListRestore } from '@/common/listRestore';
import type { TodayRecruitmentListData } from '@/components/domain/recruitment/types';
import { RECRUITMENT_SORT_DEFAULT, toRecruitmentApiRequest } from './recruitmentListQuery';
import type { RecruitmentListQuery } from './recruitmentListQuery';

const REFRESH_INTERVAL_MS = 60_000;

interface TodaySnapshot {
    queryKey: string;
    list: TodayRecruitmentListData[];
    nextRefreshAt: string | null;
    fetchedAt: number;
}

interface TodayRequest {
    scope: 'region' | 'station' | 'search';
    params: Record<string, string | string[]>;
}

/** 일반 목록과 같은 실효 조건만 사용한다. 검색은 검색어만, 지역·역은 정렬을 제외한 필터다. */
const todayRequest = (query: RecruitmentListQuery): TodayRequest => {
    if (query.search) return { scope: 'search', params: { keyword: query.search } };
    const request = toRecruitmentApiRequest({ ...query, sort: RECRUITMENT_SORT_DEFAULT });
    return { scope: request.kind, params: request.params };
};

const validItems = (list: TodayRecruitmentListData[], now: number): TodayRecruitmentListData[] =>
    list.filter((item) => {
        const expiresAt = Date.parse(item.todayExpiresAt);
        return Number.isFinite(expiresAt) && expiresAt > now;
    });

/** 일반 페이지 요청과 독립적으로 보상 목록을 유지하고, 만료·새 보상만 다시 확인한다. */
export const useTodayRecruitments = (query: RecruitmentListQuery, enabled: boolean) => {
    const request = todayRequest(query);
    const queryKey = JSON.stringify([request.scope, request.params]);
    const queryKeyRef = useRef(queryKey);
    queryKeyRef.current = queryKey;
    const { restored, save } = useListRestore<TodaySnapshot>('recruitments:today');
    const [snapshot, setSnapshot] = useState<TodaySnapshot | null>(() => {
        if (restored?.queryKey !== queryKey || !Array.isArray(restored.list)) return null;
        const list = validItems(restored.list, Date.now());
        return { ...restored, list, fetchedAt: list.length === restored.list.length ? restored.fetchedAt : 0 };
    });
    const snapshotRef = useRef(snapshot);
    snapshotRef.current = snapshot;
    const firstLoadRef = useRef(true);

    useEffect(() => {
        if (!enabled) return undefined;
        let active = true;
        let inFlight = false;
        let timer: number | undefined;
        let cached = snapshotRef.current?.queryKey === queryKey ? snapshotRef.current : null;
        let lastAttemptAt = cached?.fetchedAt ?? 0;
        const isCurrent = (): boolean => active && queryKeyRef.current === queryKey;
        const publish = (next: TodaySnapshot): void => {
            if (!isCurrent()) return;
            cached = next;
            setSnapshot(next);
        };
        const removeExpired = (): boolean => {
            if (!cached || !isCurrent()) return false;
            const list = validItems(cached.list, Date.now());
            if (list.length === cached.list.length) return false;
            publish({ ...cached, list });
            return true;
        };
        const needsRefresh = (): boolean => {
            const now = Date.now();
            const nextRefresh = cached?.nextRefreshAt ? Date.parse(cached.nextRefreshAt) : NaN;
            return now - lastAttemptAt >= REFRESH_INTERVAL_MS
                || (Number.isFinite(nextRefresh) && nextRefresh <= now && nextRefresh > lastAttemptAt);
        };
        const schedule = (): void => {
            window.clearTimeout(timer);
            if (!isCurrent() || document.visibilityState === 'hidden') return;
            const now = Date.now();
            const deadlines = cached ? [
                Date.parse(cached.nextRefreshAt ?? ''),
                ...cached.list.map((item) => Date.parse(item.todayExpiresAt)),
            ].filter((deadline) => Number.isFinite(deadline) && deadline > now) : [];
            const delay = Math.max(50, Math.min(
                inFlight ? REFRESH_INTERVAL_MS : Math.max(50, lastAttemptAt + REFRESH_INTERVAL_MS - now),
                ...deadlines.map((deadline) => deadline - now + 10),
            ));
            timer = window.setTimeout(() => {
                removeExpired();
                if (inFlight) schedule();
                else void refresh();
            }, delay);
        };
        const refresh = async (): Promise<void> => {
            if (!isCurrent() || inFlight) return;
            inFlight = true;
            lastAttemptAt = Date.now();
            // 재조회 응답이 늦어도 기존 카드의 만료 타이머는 계속 동작한다.
            schedule();
            try {
                const response = await api.recruitments.today(request.scope, request.params);
                if (!isCurrent()) return;
                if (response.code !== 0 || !Array.isArray(response.result?.list)) {
                    throw new Error(response.message || '오늘의 채용 조회 실패');
                }
                publish({
                    queryKey,
                    list: validItems(response.result.list, Date.now()),
                    nextRefreshAt: response.result.nextRefreshAt ?? null,
                    fetchedAt: Date.now(),
                });
            } catch {
                // 첫 실패·조건 변경 실패는 숨김. 같은 조건의 갱신 실패는 아직 유효한 카드만 유지한다.
                if (isCurrent()) removeExpired();
            } finally {
                inFlight = false;
                schedule();
            }
        };
        const resume = (): void => {
            if (document.visibilityState === 'hidden') {
                window.clearTimeout(timer);
                return;
            }
            const expired = removeExpired();
            if (expired || needsRefresh()) void refresh();
            else schedule();
        };
        document.addEventListener('visibilitychange', resume);
        window.addEventListener('focus', resume);
        const restoreFresh = firstLoadRef.current && cached !== null && !needsRefresh();
        firstLoadRef.current = false;
        if (restoreFresh) schedule();
        else void refresh();
        return () => {
            active = false;
            window.clearTimeout(timer);
            document.removeEventListener('visibilitychange', resume);
            window.removeEventListener('focus', resume);
        };
        // 실효 조건이 같으면 정렬·일반 페이지·화면 폭 변경으로 다시 받지 않는다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey, enabled]);

    // 일반 페이지를 이어 받는 중에도 보상 상태를 별도로 복원할 수 있다.
    useEffect(() => {
        if (snapshot?.queryKey === queryKey) save(snapshot);
    }, [snapshot, queryKey, save]);

    return {
        queryKey,
        list: enabled && snapshot?.queryKey === queryKey ? validItems(snapshot.list, Date.now()) : [],
    };
};
